"use client";

import { useEffect, useState } from "react";
import type { SearchSuggestion } from "@kraahl/contract";
import { fetchSearchSuggestions } from "@/lib/search/fetch-search-suggestions";

const DEBOUNCE_MS = 250;
const MIN_QUERY = 2;

/**
 * Debounced taxonomy autocomplete for the search bar. Race-safe via
 * AbortController (react-state-design §10): a newer keystroke cancels the
 * in-flight request so a stale response can't overwrite fresh suggestions.
 * Results are stored keyed by their query and returned only when they match
 * the current input — so we never flash a previous query's suggestions, and we
 * don't `setState` in the effect's sync body (the empty state is derived).
 * Fetch goes through the `lib/search` builder, not raw fetch (HARD GATE).
 */
export function useSearchSuggestions(
  query: string,
  enabled = true,
): { suggestions: SearchSuggestion[]; loading: boolean } {
  const [result, setResult] = useState<{ q: string; suggestions: SearchSuggestion[] }>({
    q: "",
    suggestions: [],
  });
  const [loadingFor, setLoadingFor] = useState<string | null>(null);

  const trimmed = query.trim();
  const active = enabled && trimmed.length >= MIN_QUERY;

  useEffect(() => {
    if (!active) return;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setLoadingFor(trimmed);
      fetchSearchSuggestions(trimmed, { signal: controller.signal })
        .then((suggestions) => {
          if (!controller.signal.aborted) setResult({ q: trimmed, suggestions });
        })
        .catch(() => {
          if (!controller.signal.aborted) setResult({ q: trimmed, suggestions: [] });
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoadingFor(null);
        });
    }, DEBOUNCE_MS);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [active, trimmed]);

  return {
    suggestions: active && result.q === trimmed ? result.suggestions : [],
    loading: active && loadingFor === trimmed,
  };
}
